import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Link from "next/link";
import { useRouter } from "next/router";

import { useI18n } from "@/i18n/I18nProvider";
import { containedButton, outlinedButton } from "@/utils/styles/buttons";

import UserDropDown from "../UserDropDown/UserDropDown";
import InsuranceMenu from "./menus/InsuranceMenu";
import NavPopover from "./menus/NavPopover";
import ProductsMenu from "./menus/ProductsMenu";
import {
  COMPANY_LINKS,
  INDIVIDUAL_LINKS,
  PARTNERSHIP_FORM_PATHS,
  PARTNERSHIP_URL,
  PLAY_STORE_URL,
} from "./navbar.config";
import { dropdownTrigger, mainBar } from "./navbar.styles";

/* The white bar: logo, section links and the account / download CTAs. */
export default function DesktopNav({
  isCompanyAudience,
  isLoggedIn,
  onLogout,
  allProduct,
}) {
  const { t } = useI18n();
  const router = useRouter();

  /* At most one dropdown is open, so one piece of state describes them both. */
  const [openMenu, setOpenMenu] = useState(null);

  const links = isCompanyAudience ? COMPANY_LINKS : INDIVIDUAL_LINKS;

  const openDropdown = (name) => (event) =>
    setOpenMenu({ name, anchor: event.currentTarget });

  const closeDropdown = () => setOpenMenu(null);

  /*
   * Inert while the visitor is already filling in one of the partner forms.
   * (The old check compared against window.location.pathname, which carries
   * the /en prefix, so it never matched in English.)
   */
  const goToPartnership = () => {
    if (!PARTNERSHIP_FORM_PATHS.has(router.pathname)) {
      router.push(PARTNERSHIP_URL);
    }
  };

  return (
    <>
      <Box sx={mainBar.container} className="horizontal-safe-padding">
        <Link href={isCompanyAudience ? "/companies" : "/"}>
          <Box
            component="img"
            src={t.logo}
            alt="Contact"
            width="77px"
            height="22px"
            loading="lazy"
            decoding="async"
          />
        </Link>

        <Box
          onClick={openDropdown("products")}
          sx={dropdownTrigger(openMenu?.name === "products")}
        >
          {t.secondary_nav.financial_services}
          <KeyboardArrowDownIcon />
        </Box>

        <Box
          onClick={openDropdown("insurance")}
          sx={dropdownTrigger(openMenu?.name === "insurance")}
        >
          {t.secondary_nav.insurance_services}
          <KeyboardArrowDownIcon />
        </Box>

        {links.map(({ id, href, label, icon, external }) => (
          <Box
            key={id}
            {...(external
              ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
              : { component: Link, href })}
            sx={mainBar.link}
          >
            {label(t)}
            {icon && (
              <Box
                component="img"
                src={icon}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                width={35}
                height={35}
                sx={{ width: 35, height: 35 }}
              />
            )}
          </Box>
        ))}

        {/* The trailing CTAs sit at the far end of the row. */}
        {isCompanyAudience ? (
          <Button
            variant="contained"
            aria-label="become a partner"
            onClick={goToPartnership}
            sx={{ ...containedButton, marginInlineStart: "auto" }}
          >
            {t.navbar_partnership}
          </Button>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginInlineStart: { xs: 0, md: "auto" },
              }}
            >
              {isLoggedIn ? (
                <UserDropDown onLogout={onLogout} />
              ) : (
                <Button
                  variant="outlined"
                  aria-label="Login button"
                  component={Link}
                  href="/login"
                  sx={outlinedButton}
                >
                  {t.secondary_nav.login}
                </Button>
              )}
            </Box>

            <Button
              variant="contained"
              aria-label="Download app button"
              component="a"
              target="_blank"
              rel="noopener noreferrer"
              href={PLAY_STORE_URL}
              sx={containedButton}
            >
              {t.secondary_nav.download_app}
            </Button>
          </>
        )}
      </Box>

      <NavPopover
        anchorEl={openMenu?.anchor ?? null}
        onClose={closeDropdown}
        offsetTop={3}
      >
        {openMenu?.name === "insurance" ? (
          <InsuranceMenu onNavigate={closeDropdown} />
        ) : (
          <ProductsMenu
            allProduct={allProduct}
            isCompanyAudience={isCompanyAudience}
            onNavigate={closeDropdown}
          />
        )}
      </NavPopover>
    </>
  );
}
